import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Calendar, CheckCircle, XCircle, Clock, AlertCircle, User } from 'lucide-react';
import { formatDate } from '../../utils/dateTimeUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ManageLeave = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchPendingApplications();
  }, []);

  const fetchPendingApplications = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/leave-management/pending-leave-applications`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setApplications(data);
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (applicationId) => {
    setActionLoading(applicationId);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/leave-management/approve-leave/${applicationId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({})
        }
      );

      if (response.ok) {
        setMessage({ type: 'success', text: 'Leave application approved successfully!' });
        fetchPendingApplications();
      } else {
        const data = await response.json();
        setMessage({ type: 'error', text: data.detail || 'Failed to approve' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'An error occurred' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      alert('Please provide a rejection reason');
      return;
    }

    setActionLoading(selectedApplication?.id);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/leave-management/reject-leave/${selectedApplication.id}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ reason: rejectionReason })
        }
      );

      if (response.ok) {
        setMessage({ type: 'success', text: 'Leave application rejected' });
        setShowRejectModal(false);
        setRejectionReason('');
        setSelectedApplication(null);
        fetchPendingApplications();
      } else {
        const data = await response.json();
        setMessage({ type: 'error', text: data.detail || 'Failed to reject' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'An error occurred' });
    } finally {
      setActionLoading(null);
    }
  };

  const openRejectModal = (application) => {
    setSelectedApplication(application);
    setShowRejectModal(true);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Manage Leave Applications</h1>
        <p className="text-gray-600 mt-1">Review and approve/reject pending leave requests</p>
      </div>

      {message.text && (
        <div
          className={`mb-4 p-4 rounded-lg flex items-center gap-2 ${
            message.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {message.type === 'success' ? (
            <CheckCircle className="h-5 w-5" />
          ) : (
            <AlertCircle className="h-5 w-5" />
          )}
          {message.text}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Pending Applications</span>
            <Badge variant="secondary">{applications.length} pending</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : applications.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Clock className="h-16 w-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium">No pending applications</p>
              <p className="text-sm mt-1">All caught up! No leave requests to review.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {applications.map((application) => (
                <div
                  key={application.id}
                  className="border rounded-lg p-6 hover:shadow-lg transition-shadow bg-white"
                >
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="bg-blue-100 p-3 rounded-full">
                        <User className="h-6 w-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">{application.staff_name}</h3>
                        <p className="text-sm text-gray-600">ID: {application.employee_id}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold text-blue-600">
                        {application.total_days}
                      </div>
                      <div className="text-xs text-gray-500">
                        day{application.total_days > 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>

                  {/* Leave Details */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
                    <div>
                      <label className="text-xs text-gray-500 font-medium">Leave Type</label>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge
                          variant="outline"
                          style={{
                            borderColor: application.leave_type_code === 'CL' ? '#4CAF50' : '#FF9800',
                            color: application.leave_type_code === 'CL' ? '#4CAF50' : '#FF9800'
                          }}
                        >
                          {application.leave_type_name}
                        </Badge>
                        {application.is_paid ? (
                          <span className="text-xs text-green-600 font-medium">💰 Paid</span>
                        ) : (
                          <span className="text-xs text-red-600 font-medium">🚫 Unpaid</span>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 font-medium">From Date</label>
                      <div className="flex items-center gap-2 mt-1 text-sm font-medium">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        {formatDate(application.from_date)}
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 font-medium">To Date</label>
                      <div className="flex items-center gap-2 mt-1 text-sm font-medium">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        {formatDate(application.to_date)}
                      </div>
                    </div>
                  </div>

                  {application.is_half_day && (
                    <div className="mb-4">
                      <Badge variant="secondary">
                        Half Day - {application.half_day_type === 'first_half' ? 'First Half' : 'Second Half'}
                      </Badge>
                    </div>
                  )}

                  {/* Reason */}
                  <div className="mb-4">
                    <label className="text-xs text-gray-500 font-medium">Reason</label>
                    <p className="text-sm text-gray-700 mt-1 p-3 bg-gray-50 rounded border">
                      {application.reason}
                    </p>
                  </div>

                  {/* Applied Date */}
                  <div className="text-xs text-gray-500 mb-4">
                    Applied on: {formatDate(application.applied_at)}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3 justify-end pt-4 border-t">
                    <Button
                      onClick={() => openRejectModal(application)}
                      variant="outline"
                      className="gap-2 text-red-600 border-red-300 hover:bg-red-50"
                      disabled={actionLoading === application.id}
                    >
                      <XCircle className="h-4 w-4" />
                      Reject
                    </Button>
                    <Button
                      onClick={() => handleApprove(application.id)}
                      className="gap-2 bg-green-600 hover:bg-green-700"
                      disabled={actionLoading === application.id}
                    >
                      <CheckCircle className="h-4 w-4" />
                      {actionLoading === application.id ? 'Processing...' : 'Approve'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Rejection Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Reject Leave Application</h3>
            <p className="text-sm text-gray-600 mb-4">
              Please provide a reason for rejecting this leave application:
            </p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent mb-4"
              placeholder="Enter rejection reason..."
            />
            <div className="flex gap-3 justify-end">
              <Button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectionReason('');
                  setSelectedApplication(null);
                }}
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                onClick={handleReject}
                className="bg-red-600 hover:bg-red-700"
                disabled={!rejectionReason.trim()}
              >
                Confirm Rejection
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageLeave;